import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { GoogleOAuthProvider, CredentialResponse } from '@react-oauth/google';
import { toast } from '@/components/ui/use-toast';
import { googleAuth, validateToken } from '@/api/auth';

interface User {
  id: string;
  email: string;
  name: string;
}

interface AuthContextType {
  user: User | null;
  handleGoogleLoginSuccess: (response: CredentialResponse) => void;
  handleLoginError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID ?? '722141165891-f5mn3qfftkql8ogor2b0m5jor5f4s9kd.apps.googleusercontent.com';

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    const storedToken = localStorage.getItem('access_token');
    if (storedUser && storedToken) {
      (async () => {
        try {
          const parsedUser = JSON.parse(storedUser);
          const validated = await validateToken(storedToken);
          if (validated.isValid && validated.user === parsedUser) {
            setUser(parsedUser);
            navigate('/');
          }
        } catch (error) {
          console.error('Failed to parse stored user data:', error);
          localStorage.removeItem('user');
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
        }
      })();
    }
  }, []);

  const handleGoogleLoginSuccess = async (response: CredentialResponse) => {
    try {
      if (!response.credential) {
        throw new Error('malformed CredentialResponse');
      };

      const data = await googleAuth(response.credential);

      localStorage.setItem('access_token', data.accessToken);
      localStorage.setItem('refresh_token', data.refreshToken);
      localStorage.setItem('user', JSON.stringify(data.user));

      setUser(data.user);
      navigate('/');
    } catch (error) {
      console.error(`Error in handleGoogleLoginSuccess: ${error}`);
      toast({
        title: "Error",
        description: "Failed to login with Google",
        variant: "destructive",
      });
    }
  };

  const handleLoginError = () => {
    console.error('Google login error');
  };

  return (
    <AuthContext.Provider value={{ user, handleGoogleLoginSuccess, handleLoginError }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProviderWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <AuthProvider>{children}</AuthProvider>
    </GoogleOAuthProvider>
  );
}; 
